import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import AdminDashboard from "./pages/AdminDashboard";
import KPIMetrics from "./pages/KPIMetrics";
import CAPAManagement from "./pages/CAPAManagement";
import ISOAudit from "./pages/ISOAudit";
import Tasks from "./pages/TaskManagement";
import Feedback from "./pages/Feedback";
import Reports from "./pages/Reports";
import IntegratedDocumentSOP from "./pages/IntegratedDocumentSOP";
import DocumentControl from "./pages/DocumentControl";
import EquipmentManagement from "./pages/EquipmentManagement";
import CertificationTracking from "./pages/CertificationTracking";
import EmployeeManagement from "./pages/EmployeeManagement";
import BMSAdministration from "./pages/BMSAdministration";
import DocumentApprovals from "./pages/DocumentApprovals";
import ONCRManagement from "./pages/ONCRManagement";
import NCRManagement from "./pages/NCRManagement";
import VendorManagement from "./pages/VendorManagement";
import ChangeManagement from "./pages/ChangeManagement";
import Approvals from "./pages/Approvals";
import CommandCenter from "./pages/CommandCenter";
import ViewerDashboard from "./pages/ViewerDashboard";
import EmailSettings from "./pages/EmailSettings";
 
function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();
 
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center space-y-3">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
          <p className="text-gray-600 text-sm">Loading...</p>
        </div>
      </div>
    );
  }
 
  if (!user) {
    return <Navigate to="/login" />;
  }
 
  // Role-based access check
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" />;
  }
 
  return children;
}
 
function AppRoutes() {
  const { user } = useAuth();
 
  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" /> : <Login />} />
      <Route path="/*" element={
        <ProtectedRoute>
          <Layout>
            <Routes>
              <Route path="/" element={<Navigate to="/kpis" replace />} />
              <Route path="/admin" element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <AdminDashboard />
                </ProtectedRoute>
              } />
              <Route path="/kpis" element={<KPIMetrics />} />
              <Route path="/capas" element={<CAPAManagement />} />
              <Route path="/audit" element={<ISOAudit />} />
              <Route path="/oncrs" element={<ONCRManagement />} />
              <Route path="/ncrs" element={<NCRManagement />} />
              <Route path="/vendors" element={<VendorManagement />} />
              <Route path="/equipment" element={<EquipmentManagement />} />
              <Route path="/certifications" element={<CertificationTracking />} />
              <Route path="/employees" element={
                <ProtectedRoute allowedRoles={['admin', 'manager']}>
                  <EmployeeManagement />
                </ProtectedRoute>
              } />
              <Route path="/bms-admin" element={<BMSAdministration />} />
              <Route path="/approvals" element={<DocumentApprovals />} />
              <Route path="/tasks" element={<Tasks />} />
              <Route path="/feedback" element={<Feedback />} />
              <Route path="/reports" element={<Reports />} />
              <Route path="/documents" element={<DocumentControl />} />
              <Route path="/sop-documents" element={<IntegratedDocumentSOP />} />
              <Route path="/change-management" element={<ChangeManagement />} />
              <Route path="/document-approvals" element={<Approvals />} />
              <Route path="/command-center" element={
                <ProtectedRoute allowedRoles={['admin', 'qa_manager', 'manager']}>
                  <CommandCenter />
                </ProtectedRoute>
              } />
              <Route path="/document-portal" element={<ViewerDashboard />} />
              <Route path="/email-settings" element={
                <ProtectedRoute allowedRoles={['admin', 'qa_manager']}>
                  <EmailSettings />
                </ProtectedRoute>
              } />
              {/* Catch-all redirect to dashboard */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Layout>
        </ProtectedRoute>
      } />
    </Routes>
  );
}
 
function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
 
export default App;
